// React imports
import React from 'react'

// External SDK imports
import {
  useChain,
  useDetailedBetslip,
  BetslipDisableReason,
} from '@azuro-org/sdk'

// Internal hooks and utilities
import { constants } from 'helpers'
import { SYSTEM_BET_MINIMUM_MARKETS } from '@/Overtime/constants/markets'
import { useDetailedOvertimeBetslip } from '@/contexts/DetailedOvertimeBetslipContext'
import useChainProvider from '@/hooks/useChainProvider'
import { useTokenSelection } from '@/contexts/TokenSelectionContext'

// Components
import { Icon } from 'components/ui'
import { Input } from 'components/inputs'

// Chain configuration (same as Balance component)
const CHAIN_CONFIGS = [
  {
    id: 8453, // Base
    name: 'Base',
    currency: 'USDC',
    icon: 'networks/base',
    betTokenIcon: 'currency/usdc',
    betTokenAddress: '',
  },
  {
    id: 8453, // Base
    name: 'Base',
    currency: 'WETH',
    icon: 'networks/base',
    betTokenIcon: 'currency/weth',
    betTokenAddress: '',
  },
  {
    id: 137, // Polygon
    name: 'Polygon',
    currency: 'USDT',
    icon: 'networks/polygon',
    betTokenIcon: 'currency/usdt',
    betTokenAddress: '',
  },
  {
    id: 10, // Optimism
    name: 'Optimism',
    currency: 'USDC',
    icon: 'networks/op',
    betTokenIcon: 'currency/usdc',
    betTokenAddress: '',
  },
  {
    id: 42161, // Arbitrum
    name: 'Arbitrum',
    currency: 'USDC',
    icon: 'networks/arbitrum',
    betTokenIcon: 'currency/usdc',
    betTokenAddress: '',
  },
  {
    id: 100, // Gnosis
    name: 'Gnosis',
    currency: 'WXDAI',
    icon: 'networks/gnosis',
    betTokenIcon: 'currency/wxdai',
    betTokenAddress: '',
  },
  {
    id: 88888, // Chiliz
    name: 'Chiliz',
    currency: 'WCHZ',
    icon: 'networks/chiliz',
    betTokenIcon: 'currency/wchz',
    betTokenAddress: '',
  },
]

type AmountInputProps = {
  isEnoughBalance: boolean
}

const AmountInput: React.FC<AmountInputProps> = ({ isEnoughBalance }) => {
  const { appChain } = useChain()
  const { isOvertimeChain, isAzuroChain, chainId } = useChainProvider()
  const { selectedTokenIndex } = useTokenSelection()
  const {
    betAmount: azuroBetAmount,
    changeBetAmount: azuroChangeBetAmount,
    disableReason: azuroDisableReason,
  } = useDetailedBetslip()

  const {
    betAmount: overtimeBetAmount,
    changeBetAmount: overtimeChangeBetAmount,
    disableReason: overtimeDisableReason,
  } = useDetailedOvertimeBetslip()

  // Determine the provider and return appropriate data
  const getProviderData = () => {
    switch (true) {
      case isOvertimeChain:
        return {
          betAmount: overtimeBetAmount,
          changeBetAmount: overtimeChangeBetAmount,
          disableReason: overtimeDisableReason,
        }

      case isAzuroChain:
        return {
          betAmount: azuroBetAmount,
          changeBetAmount: azuroChangeBetAmount,
          disableReason: azuroDisableReason,
        }

      default:
        // Fallback to Azuro data
        return {
          betAmount: azuroBetAmount,
          changeBetAmount: azuroChangeBetAmount,
          disableReason: azuroDisableReason,
        }
    }
  }

  const { betAmount, changeBetAmount, disableReason } = getProviderData()

  const handleChange = (value: string) => {
    const [int, digits] = value.split('.')

    if (digits) {
      value = `${int}.${digits.substring(0, 2)}`
    }

    changeBetAmount(value)
  }

  const isError = Boolean(
    !isEnoughBalance ||
      (disableReason &&
        [
          BetslipDisableReason.BetAmountGreaterThanMaxBet,
          BetslipDisableReason.BetAmountLowerThanMinBet,
        ].includes(disableReason as BetslipDisableReason)) ||
      (isOvertimeChain &&
        Number(betAmount) > 0 &&
        isOvertimeChain &&
        Number(betAmount) < SYSTEM_BET_MINIMUM_MARKETS)
  )

  const getCurrentCurrencyIcon = () => {
    if (chainId === 8453) {
      // For Base chain, use the selected token's currency icon
      const currentTokenType = selectedTokenIndex === 0 ? 'USDC' : 'WETH'
      const chainConfig = CHAIN_CONFIGS.find(
        config => config.id === chainId && config.currency === currentTokenType
      )
      return chainConfig?.betTokenIcon
    }
    // For other chains, use the default currency icon
    return constants.currencyIcons[chainId as any]
  }

  const currencyIcon = getCurrentCurrencyIcon()

  return (
    <Input
      className="rounded-b-ssm"
      type="number"
      value={betAmount}
      placeholder="0.00"
      maxValue="129"
      leftNode={<Icon className="size-5 mr-2" name={currencyIcon} />}
      onChange={handleChange}
      isError={isError}
    />
  )
}

export default AmountInput
